import { useEffect, useState } from 'react';
import { useGate, useStore } from 'effector-react';
import { useImmer } from 'use-immer';
import { flagToBool, toFlag } from 'pano-utils';
import { clone } from 'ramda';
import { nanoid } from 'nanoid';
import {
  SceneType,
  type TagLine,
  TagLineAlignMode,
  TagLinePointMode,
} from '@types';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import IconReset from '@components/icons/IconReset';
import Textarea from '@components/Textarea';
import Title from '@components/Title';
import PanelBase from '@modules/panel/PanelBase';
import { $GateEditingTagLine } from '@stores/editingStores';
import { Validator } from '@components/Validator';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import FeatureToggle from '@components/FeatureToggle';
import Slider from '@components/Slider';
import { $editingTagLineApi } from '@stores/editingTagLine';
import onPlayerClick from '@modules/hotspot/actions/onPlayerClick';
import { MainPlayer } from '@stores/krpPlayerStores';
import { $loadedScene } from '@stores/loadedStore';
import {
  TAGLINE_DEFAULT,
  TAGLINE_DEFAULT_STYLE_SETTING,
  TAGLINE_DEFAULT_TITLE_SETTING,
} from '../constants';
import { addTagLine, removeTagLine, updateTagLine } from '../krpTagLineApi';

export interface PanelTagLineProps {
  defaultValue?: TagLine;
  index: number;
  onConfirm: (index: number, tagLine: TagLine) => void;
  onDeleteClick: (index: number) => void;
  onRequestClose: () => void;
}

const fontItems: DropdownItem[] = [
  { id: 12, name: '12px' },
  { id: 14, name: '14px' },
  { id: 16, name: '16px' },
  { id: 18, name: '18px' },
  { id: 24, name: '24px' },
];

const alingModeItems: DropdownItem<TagLineAlignMode>[] = [
  { id: TagLineAlignMode.middle, name: '标尺中间' },
  { id: TagLineAlignMode.above, name: '标尺上方' },
  { id: TagLineAlignMode.bellow, name: '标尺下方' },
];

const pointModeItems: DropdownItem<TagLinePointMode>[] = [
  { id: TagLinePointMode.circle, name: '圆点' },
  //   { id: TagLinePointMode.arrow, name: '箭头' },
  { id: TagLinePointMode.square, name: '端线' },
  { id: TagLinePointMode.none, name: '无' },
];

const pointModeItemsMatrix: DropdownItem<TagLinePointMode>[] = [
  { id: TagLinePointMode.circle, name: '圆点' },
  { id: TagLinePointMode.none, name: '无' },
];

export function PanelTagLine({
  defaultValue = TAGLINE_DEFAULT,
  index,
  onConfirm,
  onRequestClose,
  onDeleteClick,
}: PanelTagLineProps) {
  const scene = useStore($loadedScene)!;
  const isNew = defaultValue === TAGLINE_DEFAULT;
  if (isNew) {
    defaultValue = clone(defaultValue);
    defaultValue.id = `tl_${nanoid()}`;
    if (scene.type === SceneType.matrix) {
      defaultValue.titleSetting.distorted = 0;
      defaultValue.style.pointMode = 1;
    }
  }
  const [state, setState] = useImmer(defaultValue);
  const [intervalId, setIntervalId] = useState<number | null>(null);

  useGate($GateEditingTagLine, state);

  const textareaHandler = useTextareaHandelrs('trimRight', (val) => {
    setState((draft) => {
      draft.title = val;
    });
  });

  const startUpdateLine = () => {
    const id = window.setInterval(() => {
      const { ath = 0, atv = 0 } =
        MainPlayer.get()?.mousePositionToSphere() || {};
      MainPlayer.get()?.updateLine({
        id: state.id,
        // 鼠标位置抖动, 取两位小数
        endAth: Number.parseFloat(Number(ath).toFixed(2)),
        endAtv: Number.parseFloat(Number(atv).toFixed(2)),
      });
    }, 100);
    setIntervalId(id);
    return id;
  };
  const stopUpdateLine = () => {
    if (intervalId != null) {
      window.clearInterval(intervalId);
    }
  };

  const onLineMoving = (e: { id: string; points: any[] }) => {
    const pos = MainPlayer.get()?.calcLineMiddlePoint(state.id);
    if (state.titleSetting.enableRotate === 0) {
      const rotate = MainPlayer.get()?.calcLineDegree(state.id);
      MainPlayer.get()?.updateTextHotspot({
        id: `${state.id}_title`,
        ath: pos?.ath,
        atv: pos?.atv,
        rotate,
      });
    } else {
      MainPlayer.get()?.updateTextHotspot({
        id: `${state.id}_title`,
        ath: pos?.ath,
        atv: pos?.atv,
      });
    }
  };

  const onLineMoveEnd = (e: { id: string; points: any[] }) => {
    setState((draft) => {
      draft.startAth = e.points[0].ath;
      draft.startAtv = e.points[0].atv;
      draft.endAth = e.points[1].ath;
      draft.endAtv = e.points[1].atv;
    });
  };

  useEffect(() => {
    return () => {
      if (isNew) {
        removeTagLine(state.id);
        $editingTagLineApi.set(null);
      }
    };
  }, []);

  useEffect(() => {
    const unwatch0 = onPlayerClick.watch(({ ath, atv }) => {
      if (state.startAth === 0 && state.startAtv === 0) {
        setState((draft) => {
          draft.startAth = ath;
          draft.startAtv = atv;
        });
        addTagLine(state, false, false, {
          onLineMoving,
          onLineMoveEnd,
        });

        startUpdateLine();
      } else if (state.endAth === 0 && state.endAtv === 0) {
        setState((draft) => {
          draft.endAth = ath;
          draft.endAtv = atv;
        });
        updateTagLine(state, true, true);
      }
    });

    if (state.endAth !== 0 || state.endAtv !== 0) {
      unwatch0();
      stopUpdateLine();
    }

    if (state.endAth !== 0 && state.endAtv !== 0) {
      updateTagLine(state, true, undefined, {
        onLineMoving,
        onLineMoveEnd,
      });
    }

    $editingTagLineApi.set({
      tagLine: state,
      isNew,
    });

    return () => {
      updateTagLine(state, undefined, undefined, {
        onLineMoving: null,
        onLineMoveEnd: null,
      });
      unwatch0();
      stopUpdateLine();
    };
  }, [state, intervalId]);

  const renderTitle = () => {
    return (
      <Collapse title="标尺文字">
        <Textarea
          scheme="dark"
          className="h-115"
          placeholder="请输入标尺文字"
          maxLength={50}
          value={state.title}
          {...textareaHandler}
        />
      </Collapse>
    );
  };

  const renderTitleSetting = () => {
    return (
      <Collapse title="文字样式">
        <div className="flex items-center justify-between">
          <div>字号</div>
          <IconReset
            onClick={() => {
              setState((draft) => {
                draft.titleSetting.fontSize =
                  TAGLINE_DEFAULT_TITLE_SETTING.fontSize;
              });
            }}
          />
        </div>
        <Dropdown
          className="mt-10"
          items={fontItems}
          selected={state.titleSetting.fontSize}
          onChange={(val) => {
            setState((draft) => {
              draft.titleSetting.fontSize = val;
              TAGLINE_DEFAULT.titleSetting.fontSize = val;
            });
          }}
        />
        <ColorPicker
          className="mt-20"
          colorString={state.titleSetting.fontColor}
          onChange={(str) =>
            setState((draft) => {
              draft.titleSetting.fontColor = str;
              TAGLINE_DEFAULT.titleSetting.fontColor = str;
            })
          }
        >
          <ColorPicker.Header>
            <div>字体颜色</div>
            <ColorPicker.Reset
              defaultColor={TAGLINE_DEFAULT_TITLE_SETTING.fontColor!}
            />
          </ColorPicker.Header>
          <ColorPicker.Bar />
        </ColorPicker>
        <ColorPicker
          className="mt-20"
          colorString={state.titleSetting.bgColor}
          onChange={(str) =>
            setState((draft) => {
              draft.titleSetting.bgColor = str;
              TAGLINE_DEFAULT.titleSetting.bgColor = str;
            })
          }
        >
          <ColorPicker.Header>
            <div>背景颜色</div>
            <ColorPicker.Reset
              defaultColor={TAGLINE_DEFAULT_TITLE_SETTING.bgColor!}
            />
          </ColorPicker.Header>
          <ColorPicker.Bar />
        </ColorPicker>

        <div className="mt-20 mb-10">文字位置</div>
        <Dropdown
          selected={state.titleSetting.alignMode}
          items={alingModeItems}
          onChange={(alignModeId) => {
            setState((draft) => {
              draft.titleSetting.alignMode = alignModeId;
              TAGLINE_DEFAULT.titleSetting.alignMode = alignModeId;
            });
          }}
        />
        {scene.type !== SceneType.matrix && (
          <FeatureToggle
            className="mt-20"
            title="跟随画面同步缩放"
            isOn={flagToBool(state.titleSetting.distorted)}
            onToggle={(isOn) => {
              setState((draft) => {
                draft.titleSetting.distorted = toFlag(isOn);
                TAGLINE_DEFAULT.titleSetting.distorted = toFlag(isOn);
              });
            }}
          />
        )}
        <FeatureToggle
          className="mt-20"
          title="自定义设置文字角度"
          isOn={flagToBool(state.titleSetting.enableRotate)}
          onToggle={(isOn) => {
            setState((draft) => {
              draft.titleSetting.enableRotate = toFlag(isOn);
              TAGLINE_DEFAULT.titleSetting.enableRotate = toFlag(isOn);
            });
          }}
        />
        {state.titleSetting.enableRotate === 1 && (
          <Slider
            className="mt-20"
            min={0}
            max={360}
            valToString={(val) => `${val}`}
            value={state.titleSetting.rotate}
            onChange={(val) => {
              setState((draft) => {
                draft.titleSetting.rotate = val;
                TAGLINE_DEFAULT.titleSetting.rotate = val;
              });
            }}
          >
            <Slider.Header title="旋转角度" />
            <Slider.Bar />
            <Slider.Control />
          </Slider>
        )}
      </Collapse>
    );
  };

  const renderLineSetting = () => {
    return (
      <Validator
        value={state}
        validFn={(value) => value.endAth !== 0 && value.endAtv !== 0}
        warningMessage="请在全景中绘制完标尺以继续。"
      >
        {({ value }) => (
          <Collapse title="线段样式">
            <Slider
              className="mt-20"
              min={1}
              max={10}
              valToString={(val) => `${val}px`}
              value={state.style.lineWidth}
              onChange={(val) => {
                setState((draft) => {
                  draft.style.lineWidth = val;
                  TAGLINE_DEFAULT.style.lineWidth = val;
                });
              }}
            >
              <Slider.Header title="线段粗细" />
              <Slider.Bar />
              <Slider.Control />
            </Slider>
            <ColorPicker
              className="mt-20"
              colorString={state.style.lineColor}
              onChange={(str) =>
                setState((draft) => {
                  draft.style.lineColor = str;
                  TAGLINE_DEFAULT.style.lineColor = str;
                })
              }
            >
              <ColorPicker.Header>
                <div>线段颜色</div>
                <ColorPicker.Reset
                  defaultColor={TAGLINE_DEFAULT_STYLE_SETTING.lineColor!}
                />
              </ColorPicker.Header>
              <ColorPicker.Bar />
            </ColorPicker>
            <div className="mt-20 mb-10">线段端点</div>
            <Dropdown
              selected={state.style.pointMode}
              items={
                scene.type === SceneType.matrix
                  ? pointModeItemsMatrix
                  : pointModeItems
              }
              onChange={(pointModeId) => {
                setState((draft) => {
                  draft.style.pointMode = pointModeId;
                  TAGLINE_DEFAULT.style.pointMode = pointModeId;
                });
              }}
            />
            <ColorPicker
              className="mt-20"
              colorString={state.style.pointColor}
              onChange={(str) =>
                setState((draft) => {
                  draft.style.pointColor = str;
                  TAGLINE_DEFAULT.style.pointColor = str;
                })
              }
            >
              <ColorPicker.Header>
                <div>端点颜色</div>
                <ColorPicker.Reset
                  defaultColor={TAGLINE_DEFAULT_STYLE_SETTING.pointColor!}
                />
              </ColorPicker.Header>
              <ColorPicker.Bar />
            </ColorPicker>
          </Collapse>
        )}
      </Validator>
    );
  };

  return (
    <PanelBase
      isChanged={() => defaultValue !== state}
      title={<Title>{isNew ? '添加标尺' : '编辑标尺'}</Title>}
      onRequestClose={() => {
        onRequestClose();
        if (isNew) {
          removeTagLine(state.id);
        }
      }}
      onConfirm={() => onConfirm(index, state)}
      onDeleteClick={() => {
        onDeleteClick(index);
        if (isNew) {
          removeTagLine(state.id);
        }
      }}
    >
      {renderTitle()}
      {renderTitleSetting()}
      {renderLineSetting()}
    </PanelBase>
  );
}
